import app from 'flarum/forum/app';
import {ClassComponent, Vnode, VnodeDOM} from 'mithril';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Tooltip from 'flarum/common/components/Tooltip';
import icon from 'flarum/common/helpers/icon';
import ComposerState from 'flarum/forum/states/ComposerState';
import Uploader, {Upload} from '../states/Uploader';
import UploadModal from '../modals/UploadModal';

interface ComposerAttachmentsAttrs {
    uploader: Uploader
    composer: ComposerState & {
        fields: {
            cimaiseExcludedPictures: string[]
        }
    }
}

export default class ComposerAttachments implements ClassComponent<ComposerAttachmentsAttrs> {
    deletingAll: boolean = false

    view(vnode: Vnode<ComposerAttachmentsAttrs>) {
        const {uploader, composer} = vnode.attrs;

        return m('.CimaiseComposerAttachmentsWrapper', m('.CimaiseComposerAttachments', {
            onclick: (event: MouseEvent) => {
                event.redraw = false;

                // When clicking in the empty area behind the attachments, users will expect the textarea to focus
                // But the textarea isn't actually behind it so we need this custom code to achieve it
                if (event.target === (vnode as any as VnodeDOM).dom.querySelector('.CimaiseComposerAttachments')) {
                    composer.editor!.focus();
                }
            },
        }, [
            uploader.pictures.map(upload => {
                const excludedIndex = upload.picture ? composer.fields.cimaiseExcludedPictures.indexOf(upload.picture.id()!) : -1;
                const selected = excludedIndex === -1;

                return m('.CimaiseComposerAttachment', {
                    className: selected ? 'CimaiseComposerAttachment--selected' : 'CimaiseComposerAttachment--unselected',
                }, this.upload(upload, selected, () => {
                    if (selected) {
                        composer.fields.cimaiseExcludedPictures.push(upload?.picture?.id()!);
                    } else {
                        composer.fields.cimaiseExcludedPictures.splice(excludedIndex, 1);
                    }
                }));
            }),
            // Offer shortcut to delete all drafts when opening the composer if it still contains older drafts
            uploader.hasExistingDrafts ? m('button.CimaiseComposerAttachment.CimaiseComposerAttachmentAdd', {
                type: 'button',
                onclick: (event: Event) => {
                    event.preventDefault();

                    if (this.deletingAll) {
                        return;
                    }

                    this.deletingAll = true;

                    Promise.all(uploader.pictures.filter(upload => upload.picture).map(upload => {
                        return uploader.deletePicture(upload.picture!);
                    })).then(() => {
                        // Reset the list so it also removes non-picture error entries
                        // Also might be needed because the uploader.deletePicture works by index and deleting all at once messes up with the indexes
                        uploader.pictures = [];
                        uploader.hasExistingDrafts = false;
                        this.deletingAll = false;
                        m.redraw();
                    });
                },
            }, [
                icon(this.deletingAll ? 'fas fa-spinner fa-spin' : 'fas fa-trash'),
                m('span', 'Delete drafts'),
            ]) : null,
            m('button.CimaiseComposerAttachment.CimaiseComposerAttachmentAdd', {
                type: 'button',
                onclick: (event: Event) => {
                    event.preventDefault();

                    app.modal.show(UploadModal, {
                        uploader,
                        inComposer: true,
                    });
                },
            }, [
                icon('far fa-images'),
                m('span', 'Upload picture'),
            ]),
        ]));
    }

    upload(upload: Upload, checked: boolean, onchange: () => void) {
        const {picture} = upload;

        if (!picture) {
            return LoadingIndicator.component();
        }

        return [
            Tooltip.component({
                text: 'Include upload in post',
            }, m('label', {
                'aria-label': 'Include upload in post',
            }, [
                m('input', {
                    type: 'checkbox',
                    checked,
                    onchange,
                }),
                m('.pill', checked ? icon('fas fa-check') : null),
            ])),
            m('img', {
                src: picture.imageUrl(150),
                alt: 'Uploaded Image Preview',
            }),
        ];
    }
}
